<template>
  <Transition name="waitlist-fade">
    <div v-if="waitlistOpen" class="waitlist-backdrop" @click="close" />
  </Transition>
  <Transition name="waitlist-slide">
    <div
      v-if="waitlistOpen"
      class="waitlist-panel"
      role="dialog"
      aria-labelledby="waitlist-title"
      aria-modal="true"
      data-testid="waitlist-drawer"
    >
      <div class="waitlist-panel__header">
        <p id="waitlist-title" class="waitlist-panel__title">
          Join the waitlist
        </p>
        <IconButton
          form="transparent"
          icon="cross"
          class="waitlist-panel__close"
          aria-label="Close"
          @click="close"
        />
      </div>

      <template v-if="!submitted || !success">
        <p class="waitlist-panel__intro">{{ introCopy }}</p>

        <form class="waitlist-form" @submit.prevent="handleFormSubmit">
          <label class="waitlist-field">
            <span class="waitlist-field__label">Your name</span>
            <input
              v-model="name"
              type="text"
              autocomplete="given-name"
              placeholder="Enter your name"
              class="waitlist-field__input"
            />
          </label>

          <label class="waitlist-field">
            <span class="waitlist-field__label">Your email address</span>
            <input
              v-model="email"
              type="email"
              autocomplete="email"
              placeholder="name@domain.com"
              class="waitlist-field__input"
            />
            <span
              v-if="fieldErrors(v$.email).length"
              class="waitlist-field__error"
            >
              {{ fieldErrors(v$.email)[0].$message }}
            </span>
          </label>

          <div class="waitlist-field">
            <span class="waitlist-field__label">Mobile number</span>
            <div class="waitlist-field__phone-row">
              <select
                v-model="selectedCountryCode"
                class="waitlist-field__input waitlist-field__dial-code"
                aria-label="Country code"
              >
                <option
                  v-for="code in telephoneCodesOrdered"
                  :key="code.code"
                  :value="code.code"
                >
                  {{ code.dial_code }} ({{ code.code }})
                </option>
              </select>
              <input
                v-model="telephone"
                type="tel"
                autocomplete="tel-national"
                placeholder="7700 900123"
                class="waitlist-field__input"
                aria-label="Mobile number"
              />
            </div>
            <span
              v-if="fieldErrors(v$.telephone).length"
              class="waitlist-field__error"
            >
              {{ fieldErrors(v$.telephone)[0].$message }}
            </span>
          </div>

          <label class="waitlist-consent">
            <input
              v-model="consent"
              type="checkbox"
              class="waitlist-consent__input"
            />
            <span class="waitlist-consent__box" aria-hidden="true">
              <svg
                class="waitlist-consent__check"
                viewBox="0 0 10 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 4L3.5 6.5L9 1"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
              </svg>
            </span>
            <span class="waitlist-consent__text">
              I agree to receive the RATANDBOA newsletter to be the first to
              know about new collections, exclusive product launches, events and
              services available. By subscribing I agree to the RATANDBOA
              <a
                href="/pages/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                class="waitlist-consent__link"
                @click.stop
                >Privacy policy</a
              >
            </span>
          </label>

          <div
            v-if="submitted && !success"
            class="waitlist-response waitlist-response--failure"
          >
            Something went wrong, please try again later.
          </div>

          <button
            type="submit"
            class="waitlist-submit"
            :disabled="submitting"
            data-testid="waitlist-register"
          >
            Register
          </button>
        </form>
      </template>

      <template v-else>
        <p class="waitlist-panel__intro">
          Success! You'll be notified when the product comes back in stock.
        </p>
      </template>
    </div>
  </Transition>
</template>

<script>
import IconButton from "@/components/IconButton.vue";

import useVuelidate from "@vuelidate/core";
import { email, helpers, required } from "@vuelidate/validators";
import { mapGetters, mapState, mapActions } from "vuex";

import scrollLock from "@/helpers/scroll-lock";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import { bisEvent, smsCaptureEvent } from "@/helpers/events.js";
import {
  subscribeBackInStock,
  subscribeToMarketingList,
  subscribeSmsList,
} from "@/helpers/klaviyo.js";
import {
  orderTelephoneCodes,
  dialCodeForCountry,
  formatFullTelephoneNumber,
  getShopifyCountryCode,
  fetchUserCountryCode,
} from "@/helpers/telephone.js";

const FALLBACK_COUNTRY_CODE = "GB";

// Optional national number, e.g. "7700 900123" (the dial code comes from
// the country select).
const nationalNumber = (value) => {
  if (!value) return true;
  return /^[\d\s]{4,15}$/.test(value.trim());
};

export default {
  name: "WaitlistDrawer",
  components: {
    IconButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      name: "",
      email: "",
      telephone: "",
      selectedCountryCode: null,
      consent: true,
      submitting: false,
      submitted: false,
      success: false,
    };
  },
  computed: {
    ...mapState("add-to-bag", ["waitlistOpen"]),
    ...mapState("product", ["product", "selectedVariant"]),
    ...mapGetters("app", ["currencyCode"]),
    introCopy() {
      if (this.selectedVariant?.title) {
        const size = this.selectedVariant.title.split(" / ")[0];
        return `To be notified when a size ${size} becomes available, please enter your email below.`;
      }

      return "To be notified when this item becomes available, please enter your email below.";
    },
    telephoneCodesOrdered() {
      return orderTelephoneCodes();
    },
    selectedDialCode() {
      return dialCodeForCountry(
        this.telephoneCodesOrdered,
        this.selectedCountryCode
      );
    },
    normalisedPhone() {
      if (!this.telephone) return null;
      const digits = this.telephone.replace(/\D/g, "");
      if (!digits) return null;
      return formatFullTelephoneNumber(this.selectedDialCode, digits);
    },
  },
  watch: {
    waitlistOpen(open) {
      if (open) {
        this.resetForm();
        this.preselectCountryCode();
        scrollLock.enable();
      } else {
        scrollLock.disable();
      }
    },
  },
  mounted() {
    // Warm the country pre-selection so the dial code is ready the first
    // time the drawer opens.
    this.preselectCountryCode();
  },
  beforeUnmount() {
    if (this.waitlistOpen) {
      scrollLock.disable();
    }
  },
  methods: {
    ...mapActions("add-to-bag", ["closeWaitlist"]),
    close() {
      this.closeWaitlist();
    },
    resetForm() {
      this.submitting = false;
      this.submitted = false;
      this.success = false;
      this.v$.$reset();
    },
    fieldErrors(validation) {
      return validation.$dirty ? validation.$errors : [];
    },
    async preselectCountryCode() {
      // Keep the user's choice if the drawer is reopened.
      if (this.selectedCountryCode) return;

      let countryCode = getShopifyCountryCode();

      if (!countryCode) {
        try {
          countryCode = await fetchUserCountryCode();
        } catch (error) {
          countryCode = null;
        }
      }

      const isKnown = this.telephoneCodesOrdered.some(
        (code) => code.code === countryCode
      );

      this.selectedCountryCode = isKnown ? countryCode : FALLBACK_COUNTRY_CODE;
    },
    async handleFormSubmit() {
      this.v$.email.$touch();
      this.v$.telephone.$touch();

      if (this.v$.email.$invalid || this.v$.telephone.$invalid) {
        return;
      }

      if (!this.selectedVariant) {
        return;
      }

      this.submitting = true;

      try {
        const subscribed = await subscribeBackInStock({
          currencyCode: this.currencyCode.toLowerCase(),
          variantId: this.selectedVariant.id,
          email: this.email,
          phone: this.normalisedPhone,
        });

        if (!subscribed) {
          this.success = false;
          this.submitted = true;
          return;
        }

        this.success = true;

        if (this.consent) {
          await subscribeToMarketingList({
            currencyCode: this.currencyCode.toLowerCase(),
            email: this.email,
            phone: this.normalisedPhone,
            firstName: this.name.trim() || null,
            customSource: "waitlist_signup",
          });
        }

        if (this.normalisedPhone) {
          const smsSubscribed = await subscribeSmsList({
            email: this.email,
            phone: this.normalisedPhone,
          });

          if (smsSubscribed) {
            smsCaptureEvent({ location: "waitlist" });
          }
        }

        this.submitted = true;

        const { waitlistVersion = 0 } = getFeatureToggles();
        bisEvent({ waitlistVersion });

        setTimeout(() => {
          this.close();
        }, 3000);
      } catch (error) {
        this.success = false;
        this.submitted = true;
      } finally {
        this.submitting = false;
      }
    },
  },
  validations() {
    return {
      email: {
        required: helpers.withMessage(
          "Please enter a valid email address",
          required
        ),
        email: helpers.withMessage("Please enter a valid email address", email),
      },
      telephone: {
        nationalNumber: helpers.withMessage(
          "Please enter a valid mobile number",
          nationalNumber
        ),
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.waitlist-backdrop {
  position: fixed;
  inset: 0;
  z-index: 7;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur($blur-amount);
}

.waitlist-panel {
  position: fixed;
  z-index: 8;
  background: $color-white;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  // Mobile: bottom sheet
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 16px 16px 0 0;
  padding: 24px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
  gap: 24px;
  max-height: 90dvh;
  overflow-y: auto;
  box-shadow: 0 -8px 12px rgba(0, 0, 0, 0.15);

  // Desktop: right-hand side panel
  @include respond-above(md) {
    left: auto;
    top: 0;
    bottom: 0;
    width: 100%;
    max-width: 480px;
    border-radius: 0;
    padding: 40px;
    gap: 32px;
    max-height: none;
    height: 100vh;
    height: 100dvh;
    box-shadow: -8px 0 12px rgba(0, 0, 0, 0.1);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__title {
    @include editorial-body(18px);
    letter-spacing: 0.18px;
    color: $color-black;
    margin: 0;
  }

  &__close {
    padding: 0;
  }

  &__intro {
    @include editorial-body(15px);
    line-height: 1.5;
    letter-spacing: 0.075px;
    color: rgba(0, 0, 0, 0.6);
    margin: 0;
  }
}

.waitlist-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;

  @include respond-above(md) {
    gap: 24px;
  }
}

.waitlist-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e5e5;

  &__label {
    @include utility-label(11px);
    text-transform: uppercase;
    color: #767676;
  }

  &__input {
    @include editorial-body(15px);
    color: $color-black;
    border: none;
    outline: none;
    background: transparent;
    padding: 0;
    width: 100%;
    border-radius: 0;

    &::placeholder {
      color: rgba(0, 0, 0, 0.35);
    }
  }

  &__phone-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
  }

  &__dial-code {
    flex: 0 0 auto;
    width: auto;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
    // Inline chevron so the native select arrow can be hidden consistently.
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M1 1l3 3 3-3' fill='none' stroke='%23000' stroke-width='1'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right center;
    padding-right: 14px;
  }

  &__error {
    @include utility-label(11px);
    color: $color-red;
  }
}

.waitlist-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;

  @include respond-above(md) {
    gap: 12px;
  }

  &__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__box {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    border: 0.5px solid $color-black;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-black;
    margin-top: 1px;

    @include respond-above(md) {
      width: 16px;
      height: 16px;
    }
  }

  &__check {
    width: 8px;
    height: 8px;
    display: none;
  }

  &__input:checked + &__box &__check {
    display: block;
  }

  &__input:focus-visible + &__box {
    outline: 2px solid $color-black;
    outline-offset: 2px;
  }

  &__text {
    @include utility-label(11px);
    line-height: 1.4;
    letter-spacing: 0.022px;
    color: rgba(0, 0, 0, 0.6);
  }

  &__link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
}

.waitlist-response {
  @include utility-label(13px);

  &--failure {
    color: $color-red;
  }
}

.waitlist-submit {
  @include utility-button(13px);
  width: 100%;
  background: $color-black;
  color: $color-white;
  border: none;
  border-radius: 4px;
  padding: 16px;
  text-transform: uppercase;
  cursor: pointer;

  @include respond-above(md) {
    padding: 18px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}

.waitlist-fade-enter-active,
.waitlist-fade-leave-active {
  transition: opacity 0.4s ease;
}

.waitlist-fade-enter-from,
.waitlist-fade-leave-to {
  opacity: 0;
}

.waitlist-slide-enter-active,
.waitlist-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
}

.waitlist-slide-enter-from,
.waitlist-slide-leave-to {
  transform: translateY(100%);

  @include respond-above(md) {
    transform: translateX(100%);
  }
}
</style>
